import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, ArrowRight, Check, Download, FileSpreadsheet, Images, KeyRound, Mail, Menu, MessageCircle, Search, Trash2, Upload, X } from "lucide-react";
import { API, api, formatDate, isAdmin, mediaUrl, money } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/hooks/useSettings";
import { paymentMessage, waLink } from "@/lib/whatsapp";
import { LoginForm } from "@/pages/auth/LoginPage";
import { ADMIN_RESOURCES, MEDIA_ENABLED, READ_ONLY, ROLE_ACCESS } from "./resources";
import { AdminEditor, AdminUpload } from "./AdminModals";
import { HomepageLayout } from "./HomepageLayout";
import { AdminOverview } from "./AdminOverview";
import { ApplicantModal, slaLabel } from "./ApplicantModal";
import { AdminSettings } from "./AdminSettings";
import { AdminMedia } from "./AdminMedia";
import { AlbumPhotosModal, ImportMembersModal } from "./BulkModals";
import { AdminCheckin } from "./AdminCheckin";

const LoginPanel = ({ user }) => (
  <main className="login-page">
    <div className="login-panel wide">
      <span className="brand-mark">D</span>
      <span className="eyebrow">DCEA Admin</span>
      <h1>Control <span className="gold-text">room.</span></h1>
      <p>{user ? "Your account does not have admin access. Sign in with an administrator account." : "Sign in with your administrator email and password, or an authorised Google account."}</p>
      <LoginForm admin testPrefix="admin-login" />
      <Link to="/" data-testid="admin-back-home">Back to public website</Link>
    </div>
  </main>
);

const detailsFor = (resource, item) => {
  if (resource === "registrations") return `${item.event_title || item.event_id} · ${item.email || ""}`;
  if (resource === "payments") return `${item.plan_label || item.plan_key} · ${money(item.amount, item.currency)}${item.reference ? ` · Ref ${item.reference}` : ""}`;
  if (resource === "users") return `${item.email} · ${(item.role || "").replace("_", " ")}`;
  if (resource === "audit-logs") return `${item.actor || ""} · ${item.action || ""}`;
  return item.email || item.category || item.position || item.organization || item.venue || "—";
};

export default function AdminPage() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const [resource, setResource] = useState("overview");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [mobile, setMobile] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [importing, setImporting] = useState(false);
  const [album, setAlbum] = useState(null);
  const [selected, setSelected] = useState([]);
  const [checkinEvent, setCheckinEvent] = useState("");
  const nav = useNavigate();
  const meta = ADMIN_RESOURCES.find(r => r.key === resource);
  const rid = item => (resource === "users" ? item.user_id : item.id);
  const allowed = user ? ROLE_ACCESS[user.role] : null;
  const visible = ADMIN_RESOURCES.filter(r => !allowed || allowed.has(r.key));

  const load = () => {
    setErr("");
    if (meta?.custom) return;
    api(`/admin/resources/${resource}?search=${encodeURIComponent(search)}`)
      .then(d => { setItems(d.items || []); setTotal(d.total || 0); })
      .catch(e => setErr(e.message));
  };
  useEffect(() => { if (user && isAdmin(user)) load(); /* eslint-disable-next-line */ }, [user, resource, search]);

  const go = (key, extra = "") => { setResource(key); setSearch(""); setNotice(""); setMobile(false); setSelected([]); setCheckinEvent(extra); };
  const toggleSel = id => setSelected(sel => (sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]));
  const allIds = items.map(rid);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.includes(id));
  const bulk = async action => {
    const verb = action === "delete" ? "Permanently DELETE" : action === "archive" ? "Archive" : "Restore";
    if (!window.confirm(`${verb} ${selected.length} selected record(s)?${action === "delete" ? " This cannot be undone." : ""}`)) return;
    try { const r = await api(`/admin/resources/${resource}/bulk`, { method: "POST", body: JSON.stringify({ ids: selected, action }) }); flash(r.message); setSelected([]); load(); } catch (e) { setErr(e.message); }
  };
  const flash = msg => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

  const download = async (path, filename) => {
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setErr(e.message); }
  };
  const stamp = new Date().toISOString().slice(0, 10);
  const exportCsv = () => download("/admin/registrations/export", `dcea-registrations-${stamp}.csv`);

  const label = item => item.name || item.title || item.company || item.heading || item.id;
  const remove = async item => {
    if (!window.confirm(`Archive "${label(item)}"? It will be hidden from the website but can be restored.`)) return;
    try { await api(`/admin/resources/${resource}/${rid(item)}`, { method: "DELETE" }); flash("Record archived."); load(); } catch (e) { setErr(e.message); }
  };
  const destroy = async item => {
    if (!window.confirm(`Permanently DELETE "${label(item)}"? This cannot be undone.`)) return;
    try { await api(`/admin/resources/${resource}/${rid(item)}?permanent=true`, { method: "DELETE" }); flash("Record deleted permanently."); load(); } catch (e) { setErr(e.message); }
  };
  const restore = async item => {
    try { await api(`/admin/resources/${resource}/${rid(item)}`, { method: "PUT", body: JSON.stringify({ active: true }) }); flash("Record restored."); load(); } catch (e) { setErr(e.message); }
  };
  const [applicant, setApplicant] = useState(null);
  const [overviewKey, setOverviewKey] = useState(0);
  const resetPassword = async item => {
    const pw = window.prompt(`New password for ${item.email} (min 8 characters):`);
    if (!pw) return;
    try { const r = await api(`/admin/users/${item.user_id}/reset-password`, { method: "POST", body: JSON.stringify({ new_password: pw }) }); flash(r.message); } catch (e) { setErr(e.message); }
  };
  const sendSetupLink = async item => {
    try { const r = await api(`/admin/users/${item.user_id}/send-setup-link`, { method: "POST" }); flash(r.message); } catch (e) { setErr(e.message); }
  };
  const verifyPayment = async (item, action) => {
    const note = action === "reject" ? window.prompt("Reason for rejection (shown to the member):") || "" : "";
    if (action === "reject" && !window.confirm(`Reject payment from ${item.name}?`)) return;
    try { const r = await api(`/admin/payments/${item.id}/verify`, { method: "POST", body: JSON.stringify({ action, note }) }); flash(r.message + (action === "approve" && waLink(item.phone, "") ? " Use the WhatsApp button to notify the member." : "")); load(); } catch (e) { setErr(e.message); }
  };
  const markWhatsApp = async item => {
    try { await api("/admin/whatsapp/mark", { method: "POST", body: JSON.stringify({ kind: "payment", id: item.id }) }); load(); } catch (e) { setErr(e.message); }
  };

  if (user === null) return <div className="loading" data-testid="admin-loading">Checking admin access…</div>;
  if (!user || !isAdmin(user)) return <LoginPanel user={user} />;

  const canWrite = !READ_ONLY.has(resource);
  const canMedia = MEDIA_ENABLED.has(resource);
  const custom = { overview: <AdminOverview go={go} role={user.role} onReview={setApplicant} refreshKey={overviewKey} />, "homepage-sections": <HomepageLayout />, settings: <AdminSettings />, "media-library": <AdminMedia />, checkin: <AdminCheckin key={checkinEvent} initialEvent={checkinEvent} /> }[resource];

  return (
    <div className="admin-shell">
      <aside className={mobile ? "admin-sidebar mobile-open" : "admin-sidebar"}>
        <div className="admin-brand"><span className="brand-mark">D</span><b>DCEA<span>CONTROL ROOM</span></b></div>
        <nav>
          {visible.map(r => (
            <button className={resource === r.key ? "active" : ""} onClick={() => go(r.key)} key={r.key} data-testid={`admin-nav-${r.key}`}>{r.label}</button>
          ))}
        </nav>
        <button className="signout" onClick={() => logout().then(() => nav("/"))} data-testid="admin-logout-button">Sign out</button>
      </aside>
      <main className="admin-main">
        <div className="admin-top">
          <button onClick={() => setMobile(!mobile)} className="mobile-menu" data-testid="admin-mobile-menu">{mobile ? <X size={18} /> : <Menu size={18} />}</button>
          <div><span className="eyebrow">Operations dashboard</span><h1>{meta?.label || resource}</h1></div>
          <div className="admin-user">
            <img src={user.picture || `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(user.name || "A")}`} alt={user.name} />
            <span>{user.name} · <em data-testid="admin-metric-role">{(user.role || "").replace("_", " ")}</em></span>
          </div>
        </div>
        {!meta?.custom && (
          <div className="admin-toolbar">
            <div className="search-box">
              <Search size={16} />
              <input placeholder={`Search ${meta?.label.toLowerCase() || resource}…`} value={search} onChange={e => setSearch(e.target.value)} data-testid="admin-resource-search" />
            </div>
            <div className="admin-toolbar-actions">
              <span className="pill" data-testid="admin-metric-total">{total} records</span>
              {resource === "members" && <button className="button button-ghost dark" onClick={() => download("/admin/members/export?format=csv", `dcea-members-${stamp}.csv`)} data-testid="admin-export-members-csv"><Download size={14} /> CSV</button>}
              {resource === "members" && <button className="button button-ghost dark" onClick={() => download("/admin/members/export?format=xlsx", `dcea-members-${stamp}.xlsx`)} data-testid="admin-export-members-xlsx"><Download size={14} /> Excel</button>}
              {resource === "members" && <button className="button button-ghost dark" onClick={() => setImporting(true)} data-testid="admin-import-members"><FileSpreadsheet size={14} /> Import</button>}
              {resource === "registrations" && <button className="button button-ghost dark" onClick={exportCsv} data-testid="admin-export-csv"><Download size={14} /> Export CSV</button>}
              {canMedia && <button className="button button-ghost dark" onClick={() => setUploading(true)} data-testid="admin-upload-open"><Upload size={14} /> Upload media</button>}
              {canWrite && <button className="button button-gold" onClick={() => setEditing({ mode: "create", record: {} })} data-testid="admin-add-record-button">Add {meta?.label.replace(/s$| & accounts$/, "").toLowerCase() || "record"} <ArrowRight size={15} /></button>}
            </div>
          </div>
        )}
        {err && <div className="error-message" data-testid="admin-error">{err}</div>}
        {notice && <div className="success-message" data-testid="admin-notice">{notice}</div>}
        {canWrite && selected.length > 0 && (
          <div className="bulk-bar" data-testid="bulk-bar">
            <b>{selected.length} selected</b>
            <button className="button button-ghost dark" onClick={() => bulk("archive")} data-testid="bulk-archive"><Archive size={14} /> Archive</button>
            <button className="button button-ghost dark" onClick={() => bulk("restore")} data-testid="bulk-restore"><ArchiveRestore size={14} /> Restore</button>
            <button className="button button-gold danger-btn" onClick={() => bulk("delete")} data-testid="bulk-delete"><Trash2 size={14} /> Delete</button>
            <button className="link" onClick={() => setSelected([])} data-testid="bulk-clear">Clear</button>
          </div>
        )}
        {custom || (
          <div className="admin-table">
            <div className={canWrite ? "table-head selectable" : "table-head"}>
              {canWrite && <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : allIds)} aria-label="Select all" data-testid="select-all-checkbox" />}
              <span>Record</span><span>Details</span><span>Status</span><span>Actions</span>
            </div>
            {items.length ? items.map((item, i) => (
              <div className={canWrite ? "table-row selectable" : "table-row"} key={item.id || item.user_id || i} data-testid={`admin-record-${i}`}>
                {canWrite && <input type="checkbox" checked={selected.includes(rid(item))} onChange={() => toggleSel(rid(item))} aria-label="Select row" data-testid={`select-row-${i}`} />}
                <div>
                  <b>{item.name || item.title || item.company || item.heading || item.file_name || item.label || item.id}</b>
                  <small>{rid(item) || item.id}{item.created_at ? ` · ${formatDate(item.created_at)}` : ""}</small>
                </div>
                <span>{detailsFor(resource, item)}{resource === "payments" && item.proof_url && <> · <a href={mediaUrl(item.proof_url)} target="_blank" rel="noreferrer" className="link" data-testid={`admin-proof-${i}`}>receipt</a></>}</span>
                <span className={`pill ${item.status || ""} ${item.checked_in_at ? "paid" : ""}`}>{item.active === false ? "Archived" : (item.status || "Active").toString().replace("_", " ")}</span>
                {(resource === "applications" || (resource === "users" && item.role === "member")) && (item.status || "").toLowerCase() === "pending" && item.active !== false && (() => { const s = slaLabel(item.created_at); return s ? <span className={`pill sla ${s.tone}`} data-testid={`admin-sla-${i}`}>{s.text}</span> : null; })()}
                <span className="row-actions">
                  {resource === "users" && item.role === "member" && item.status === "pending" && <button className="link" onClick={() => setApplicant(item.user_id)} data-testid={`admin-approve-${i}`}><Check size={13} /> Review & approve</button>}
                  {resource === "applications" && ((item.status || "").toLowerCase() === "pending"
                    ? <button className="link" onClick={() => setApplicant(item.id)} data-testid={`admin-app-approve-${i}`}><Check size={13} /> Review & approve</button>
                    : <button className="link" onClick={() => setApplicant(item.id)} data-testid={`admin-app-view-${i}`}>View details</button>)}
                  {resource === "users" && <button className="link" onClick={() => resetPassword(item)} data-testid={`admin-reset-password-${i}`}><KeyRound size={13} /> Reset password</button>}
                  {resource === "users" && <button className="link" onClick={() => sendSetupLink(item)} data-testid={`admin-setup-link-${i}`}><Mail size={13} /> Setup link</button>}
                  {resource === "albums" && <button className="link" onClick={() => setAlbum(item)} data-testid={`admin-album-photos-${i}`}><Images size={13} /> Photos ({item.photos?.length || 0})</button>}
                  {resource === "payments" && item.status !== "paid" && item.status !== "awaiting_payment" && <button className="link" onClick={() => verifyPayment(item, "approve")} data-testid={`admin-verify-${i}`}><Check size={13} /> Verify & activate</button>}
                  {resource === "payments" && item.status === "submitted" && <button className="link danger" onClick={() => verifyPayment(item, "reject")} data-testid={`admin-reject-${i}`}>Reject</button>}
                  {resource === "payments" && item.status === "paid" && waLink(item.phone, "") && (
                    <a href={waLink(item.phone, paymentMessage(item, settings.short_name || "DCEA"))} target="_blank" rel="noreferrer" className={item.whatsapp_sent_at ? "link wa-link sent" : "link wa-link"} onClick={() => markWhatsApp(item)} title={item.whatsapp_sent_at ? `Sent ${formatDate(item.whatsapp_sent_at)}` : "Open WhatsApp with the confirmation message"} data-testid={`admin-whatsapp-${i}`}><MessageCircle size={13} /> {item.whatsapp_sent_at ? "WhatsApp ✓" : "WhatsApp"}</a>
                  )}
                  {canWrite && (
                    <>
                      <button className="link" onClick={() => setEditing({ mode: "edit", record: item })} data-testid={`admin-edit-${i}`}>Edit</button>
                      {item.active === false ? (
                        <button className="link" onClick={() => restore(item)} data-testid={`admin-restore-${i}`}><ArchiveRestore size={13} /> Restore</button>
                      ) : (
                        <button className="link" onClick={() => remove(item)} data-testid={`admin-archive-${i}`}><Archive size={13} /> Archive</button>
                      )}
                      <button className="link danger" onClick={() => destroy(item)} data-testid={`admin-delete-${i}`}><Trash2 size={13} /> Delete</button>
                    </>
                  )}
                </span>
              </div>
            )) : <div className="empty-state" data-testid="admin-empty-state">No records in this collection yet.</div>}
          </div>
        )}
      </main>
      {applicant && <ApplicantModal applicantRef={applicant} onClose={() => setApplicant(null)} onReviewed={msg => { setApplicant(null); flash(msg); load(); setOverviewKey(k => k + 1); }} />}
      {editing && <AdminEditor resource={resource} record={editing.mode === "edit" ? { ...editing.record, id: rid(editing.record) } : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportMembersModal onClose={() => setImporting(false)} onDone={load} />}
      {album && <AlbumPhotosModal album={album} onClose={() => setAlbum(null)} onChanged={() => api(`/admin/resources/albums?search=`).then(d => { setItems(d.items || []); const fresh = (d.items || []).find(a => a.id === album.id); if (fresh) setAlbum(fresh); })} />}
      {uploading && <AdminUpload onClose={() => setUploading(false)} onUploaded={() => { setUploading(false); load(); }} />}
    </div>
  );
}
